import {
	AppButton,
	Input,
	Modal,
	ModalHeader,
	Radio,
	RadioGroup
} from '@clickguard/design-system'
import { FormEvent, useEffect, useState } from 'react'
import { TEMPLATE_SCOPE_OPTIONS } from './rule-options'
import { CustomRule, TemplateScope } from './types'

interface SaveAsTemplateDialogProps {
	/** The rule being saved; null keeps the dialog closed. */
	rule: CustomRule | null
	onClose: () => void
	onSave: (name: string, scope: TemplateScope) => void
}

/** "Save as template" — turns a rule into a reusable blueprint and picks who can
 *  start from it. Copy semantics: editing either side never touches the other. */
export const SaveAsTemplateDialog = ({
	rule,
	onClose,
	onSave
}: SaveAsTemplateDialogProps) => {
	const [name, setName] = useState('')
	const [scope, setScope] = useState<TemplateScope>('shared')

	// Reseed each time a rule opens the dialog (default the template name to the
	// rule's, the tier to Shared rules — the common "share with my team" case).
	useEffect(() => {
		if (rule) {
			setName(rule.name)
			setScope('shared')
		}
	}, [rule])

	const scopeChoices = TEMPLATE_SCOPE_OPTIONS

	const canSave = Boolean(name.trim())

	const handleSubmit = (event: FormEvent) => {
		event.preventDefault()
		if (!canSave) return
		onSave(name.trim(), scope)
		onClose()
	}

	return (
		<Modal open={rule !== null} onClose={onClose} size="sm">
			{/* autoComplete off: Chrome's saved-value popup is browser chrome, so
			    a click inside the dialog cannot close it. */}
			<form
				onSubmit={handleSubmit}
				autoComplete="off"
				className="flex flex-col gap-5 p-6"
			>
				<ModalHeader
					size="sm"
					title="Save as template"
					subtitle="Reuse this rule across all this workspace's websites."
					onClose={onClose}
				/>

				<Input
					label="Template name"
					placeholder="e.g. Data center blockade"
					size="sm"
					state="default"
					value={name}
					onChange={(event) => setName(event.target.value)}
					autoComplete="off"
					autoFocus
				/>

				<div>
					<span className="mb-1.5 block text-base font-medium text-[rgb(var(--cg-text-primary))]">
						Who can use it
					</span>
					<RadioGroup
						name="template-scope"
						value={scope}
						onValueChange={(value) => setScope(value as TemplateScope)}
					>
						{scopeChoices.map((option) => (
							<Radio
								key={option.value}
								value={option.value}
								size="sm"
								label={option.label}
								description={option.hint}
							/>
						))}
					</RadioGroup>
				</div>

				<div className="flex items-center justify-end gap-3">
					<AppButton
						type="button"
						label="Cancel"
						variant="secondary"
						roundness="default"
						onClick={onClose}
					/>
					<AppButton
						type="submit"
						label="Save template"
						variant="primary"
						roundness="default"
						disabled={!canSave}
					/>
				</div>
			</form>
		</Modal>
	)
}
